/**
 * Utility functions for sharing race plans
 */

import pako from 'pako';

/**
 * Share a race plan via the API
 * 
 * @param {Object} plan - The race plan to share
 * @param {boolean} isSharing - Whether this is a temporary share or permanent storage
 * @returns {Promise<string>} - The shared plan ID
 */
export const sharePlanViaApi = async (plan, isSharing = true) => {
  try {
    console.log(`Sending plan to API for ${isSharing ? 'sharing' : 'cloud storage'}`);
    
    // Create a clean copy of the plan for sharing
    const cleanPlan = {
      ...plan,
      // Mark whether this is a share or a cloud save
      isSharing: isSharing,
      cloudStorage: !isSharing,
      // Include unique ID and timestamp
      sharedAt: new Date().toISOString()
    };
    
    console.log(`Plan size: ${JSON.stringify(cleanPlan).length} bytes`);
    
    // Make POST request to API
    const response = await fetch('/api/plans', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        plan: cleanPlan
      }),
    });
    
    if (!response.ok) {
      // Log the error and rejection reason
      console.error(`API Error: ${response.status} ${response.statusText}`);
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('API Error details:', errorText);
      throw new Error(`Failed to save plan: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data || !data.id) {
      console.error('Invalid response from API:', data);
      throw new Error('Invalid response from API');
    }
    
    console.log(`Plan saved to API with ID: ${data.id}`);
    return data.id;
  } catch (error) {
    console.error('Error sharing plan via API:', error);
    throw error;
  }
};

/**
 * Get a shared race plan from the API by ID
 * 
 * @param {string} id - The ID of the shared plan
 * @returns {Promise<Object>} - The shared plan object
 */
export const getSharedPlanFromApi = async (id) => {
  try {
    // Make API request to get the shared plan
    const response = await fetch(`/api/plans/${id}`);
    
    if (!response.ok) {
      throw new Error(`Server returned ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    // Check if plan has compressed elevation data that needs to be decompressed
    if (data.formData?._elevationCompressed && data.formData?._compressedElevationData) {
      try {
        console.log(`Decompressing shared plan elevation data (${data.formData._elevationLength} points)`);
        
        // Decompress the data
        const decompressed = pako.inflate(data.formData._compressedElevationData, { to: 'string' });
        data.formData.elevationData = JSON.parse(decompressed);
        
        // Clean up compression artifacts
        delete data.formData._compressedElevationData;
        delete data.formData._elevationCompressed;
        delete data.formData._elevationLength;
        
        console.log(`Successfully decompressed elevation data with ${data.formData.elevationData.length} points`);
      } catch (compressionError) {
        console.error('Error decompressing elevation data:', compressionError);
        // Set empty array so the application can still function
        data.formData.elevationData = [];
      }
    }
    
    // Determine if this is a shared plan (vs a cloud-stored plan)
    const isSharedPlan = 
      // If explicitly marked in the data
      data.isSharing === true || 
      // If not marked as a cloud storage plan
      data.cloudStorage !== true;
      
    console.log(`Plan type check - isSharing: ${data.isSharing}, cloudStorage: ${data.cloudStorage}, determined as: ${isSharedPlan ? 'shared plan' : 'cloud plan'}`);
    
    // Add metadata for client-side handling of shared plans
    if (isSharedPlan) {
      // Explicitly mark as a shared, read-only plan
      data.isSharedPlan = true;
      data.readOnly = true;
      data.sharedPlan = true;
      
      // Remove any cloud storage flags
      delete data.isCloudStored;
      delete data.cloudStorage;
      
      console.log('Plan marked as a read-only shared plan');
    } else {
      // This is a cloud-stored plan, ensure it's editable
      data.isCloudStored = true;
      data.cloudStorage = true;
      data.readOnly = false;
      data.isSharedPlan = false;
      
      console.log('Plan marked as an editable cloud plan');
    }
    
    return data;
  } catch (error) {
    console.error(`Error fetching shared plan with ID ${id}:`, error);
    throw error;
  }
}; 